/**
 * Military organization tiers + formation-scaled upkeep.
 *
 * The simulation/LOD structure for the 20,000+ unit target: soldiers are
 * render-only instances, but every FORMATION is a sim agent with an
 * organization tier. The tier defines:
 * - paperStrength: the establishment "on the books" — drives UPKEEP and
 *   population. The state pays by the books.
 * - fieldBodies: the bodies actually mustered/simulated/rendered. Combat
 *   power and morale scale with actual bodies.
 *
 * Design authority: workspace/goals/three-js-legionnaire-prototype/files/
 * organization/ORGANIZATION.md (finalized 2026-09-21). The tier tables here
 * are PROTOTYPE transcriptions of that proposal — canonical data lives in
 * the Unreal repo (owner's side) and public/data/* is never touched.
 *
 * Tech gates reference the PROPOSED canonical tech IDs (PATCH_SPEC.md);
 * the IDs already exist in the data mirror, so no prototype techs are
 * needed for gating. Until the owner applies the patch, the *wiring* is
 * prototype-side.
 *
 * Upkeep (proposal §1): upkeep_per_minute = K * n^0.85, n = paper
 * strength, K = 0.2 tuned so a century (100) costs 10 food/min. Sublinear:
 * organizing upward pays (10 cohorts ≈ 380/min vs 1 legion ≈ 279/min).
 *
 * Failure mode (proposal §3): unpaid formations lose morale, then desert —
 * never instant deletion.
 *
 * Three systems stay distinct:
 * - COMMAND (sim/command.ts): formation count, 1 per formation any size.
 * - POPULATION (sim/echelon.ts): represented heads (paper strength).
 * - UPKEEP (here): logistics cost per formation, food/min on paper.
 *
 * Mirrors the future UE organization/upkeep subsystem; render-free.
 */
import type { UnitDef } from "../data/types";
import type { Combatant } from "./combat";
import { applyUnpaidMorale } from "./combat";

/** PROTOTYPE TUNING: upkeep = K * n^ALPHA food/min, n = paper strength. */
export const UPKEEP_K = 0.2;
export const UPKEEP_ALPHA = 0.85;
/**
 * PROTOTYPE TUNING: training cost/time scale anchor. Before organization,
 * every formation mustered BASE_FORMATION_SIZE soldiers; a tier's training
 * cost and time scale by paperStrength / BASE_FORMATION_SIZE. Muster rolls
 * are honest — a legion costs ~83x a warband to raise.
 */
export const BASE_FORMATION_SIZE = 60;
/**
 * PROTOTYPE TUNING: arrears beyond this many minutes of the formation's own
 * upkeep start desertion.
 */
export const DESERTION_DEBT_MINUTES = 2;
/** PROTOTYPE TUNING: fraction of remaining bodies deserting per second once started. */
export const DESERTION_FRACTION_PER_SEC = 0.005;

export interface OrgTier {
  /** Stable save identifier. Never rename. */
  id: string;
  faction: string;
  name_en: string;
  /** Establishment on the books — drives upkeep n and population. */
  paperStrength: number;
  /** Bodies mustered/simulated/rendered. Combat scales with these. */
  fieldBodies: number;
  /** Proposed canonical tech ID unlocking the tier; null = baseline. */
  techGate: string | null;
  /** Minimum echelon to muster (player side; AI drills without age-up). */
  minEchelon: number;
  /**
   * Appointment slots embedded in the formation body (officers stand in
   * the ranks — never independently commanded units, never UI badges).
   * Render-time concern; the sim only records the names. Entries suffixed
   * "(visual)" are visual-only and do NOT count toward fieldBodies.
   */
  appointments: string[];
  /**
   * Rank-and-file bodies for leaf tiers (tiers with no sub-formations).
   * Provable composition: rankers + officers.length === fieldBodies,
   * where officers are non-visual appointments.
   */
  rankers?: number;
  /**
   * Sub-formations composing this tier. Provable composition:
   * Σ(count × tier.fieldBodies) + (commandGroup ?? 0) === fieldBodies.
   */
  contains?: Array<{ tierId: string; count: number }>;
  /**
   * Extra command-group bodies riding with composite tiers (e.g. the
   * legion's staff) — counted in fieldBodies, not in any sub-formation.
   */
  commandGroup?: number;
}

/**
 * PROTOTYPE transcription of ORGANIZATION.md §§2–4 + echelon_variants from
 * proposed-organization*.json (finalized 2026-09-21).
 *
 * Notes on transcription decisions (all prototype-labeled):
 * - Maniple is omitted as a muster tier (transitional organization; the
 *   century is the building block) — a data-side follow-up, not a
 *   prototype decision. E2 musters the century directly.
 * - Legion gate is tech_cohort_organisation (E3–4) per ORGANIZATION.md §2
 *   ("The legion musters E3–E4 only"); tech_field_army_doctrine gates the
 *   E5 vexillatio/field army, which is PENDING HISTORICAL REVIEW (§5) and
 *   not musterable in this prototype.
 * - Cohors prima is a muster *variant* inside the legion muster (exactly
 *   one per legion), not a standalone tier — not modeled here.
 * - Non-Roman tiers take paper = field (the proposal gives paper only);
 *   composite tiers prove field = Σ(sub-formations) + command group.
 * - Gallic shieldband (120) is an E4 *reform body*, smaller than the host
 *   (320): the auto-muster picks the largest unlocked tier, so the
 *   shieldband only musters by explicit player choice at E4.
 */
export const ORG_TIERS: OrgTier[] = [
  // ---- Rome: decimal, permanent, drill ----
  {
    id: "rome_contubernium", faction: "rome", name_en: "Contubernium",
    paperStrength: 8, fieldBodies: 8, techGate: null, minEchelon: 1,
    rankers: 8, appointments: [],
  },
  {
    // 76 rankers + centurion + optio + signifer + tesserarius = 80.
    id: "rome_century", faction: "rome", name_en: "Century",
    paperStrength: 100, fieldBodies: 80, techGate: "tech_manipular_drill",
    minEchelon: 2, rankers: 76,
    appointments: ["centurion", "optio", "signifer", "tesserarius"],
  },
  {
    // Six field-centuries = 480. The cornicen is a visual-only slot.
    id: "rome_cohort", faction: "rome", name_en: "Cohort",
    paperStrength: 480, fieldBodies: 480, techGate: "tech_cohort_organisation",
    minEchelon: 3, contains: [{ tierId: "rome_century", count: 6 }],
    appointments: ["cornicen (visual)"],
  },
  {
    // Ten cohorts (4,800) + ~12 command group = 4,812 field bodies.
    // 188 attached services live on the books only (paper 5,000).
    id: "rome_legion", faction: "rome", name_en: "Legion",
    paperStrength: 5000, fieldBodies: 4812,
    techGate: "tech_cohort_organisation", minEchelon: 3,
    contains: [{ tierId: "rome_cohort", count: 10 }], commandGroup: 12,
    appointments: [
      "legatus_legionis", "tribunus_laticlavius",
      "tribunus_militum", "tribunus_militum",
      "aquilifer", "imaginifer",
    ],
  },
  // ---- Gallic Coalitions: social, prestige muster (no Roman decimal) ----
  {
    id: "gaul_warband", faction: "gauls", name_en: "Warband",
    paperStrength: 96, fieldBodies: 96, techGate: null, minEchelon: 1,
    rankers: 95, appointments: ["chieftain"],
  },
  {
    // 3 warbands (288) + cavalry wing (32) = 320.
    id: "gaul_host", faction: "gauls", name_en: "Chieftain's Host",
    paperStrength: 320, fieldBodies: 320,
    techGate: "tech_gaul_oppidum_hall_drill", minEchelon: 2,
    contains: [{ tierId: "gaul_warband", count: 3 }], commandGroup: 32,
    appointments: ["chieftain", "champion", "boar_standard_bearer"],
  },
  {
    id: "gaul_shieldband", faction: "gauls", name_en: "Coalition Shieldband",
    paperStrength: 120, fieldBodies: 120,
    techGate: "tech_gaul_coalition_muster", minEchelon: 4,
    rankers: 118, appointments: ["chieftain", "champion"],
  },
  {
    // 4 shieldbands (480) + high king's retinue (96) = 576.
    id: "gaul_grand_host", faction: "gauls", name_en: "Grand Host",
    paperStrength: 576, fieldBodies: 576,
    techGate: "tech_gaul_grand_coalition_compact", minEchelon: 5,
    contains: [{ tierId: "gaul_shieldband", count: 4 }], commandGroup: 96,
    appointments: ["high_king", "chieftain", "champion", "boar_standard_bearer"],
  },
  // ---- Samnites: kin/oath tiers (no Roman decimal) ----
  {
    id: "samnite_warband", faction: "samnite", name_en: "Warband",
    paperStrength: 72, fieldBodies: 72, techGate: null, minEchelon: 1,
    rankers: 71, appointments: ["war_leader"],
  },
  {
    id: "samnite_linen_legion", faction: "samnite", name_en: "Linen Legion",
    paperStrength: 160, fieldBodies: 160,
    techGate: "tech_samnite_sacred_precinct_drill", minEchelon: 2,
    rankers: 157, appointments: ["war_leader", "oath_bearer", "oath_priest"],
  },
  {
    // 2 warbands (144) + war leaders' retinues (16) = 160.
    id: "samnite_contingent", faction: "samnite", name_en: "Tribal Contingent",
    paperStrength: 160, fieldBodies: 160,
    techGate: "tech_samnite_war_camp_drill", minEchelon: 3,
    contains: [{ tierId: "samnite_warband", count: 2 }], commandGroup: 16,
    appointments: ["war_leader", "oath_bearer", "oath_bound_retinue"],
  },
  {
    id: "samnite_federal_warband", faction: "samnite", name_en: "Federal Warband",
    paperStrength: 96, fieldBodies: 96,
    techGate: "tech_samnite_federal_muster", minEchelon: 4,
    rankers: 94, appointments: ["war_leader", "oath_bearer"],
  },
  {
    // 4 federal warbands (384) + oath-bound reserve (96) = 480.
    id: "samnite_layered_host", faction: "samnite", name_en: "Layered Host",
    paperStrength: 480, fieldBodies: 480,
    techGate: "tech_samnite_depth_defence", minEchelon: 5,
    contains: [{ tierId: "samnite_federal_warband", count: 4 }],
    commandGroup: 96,
    appointments: ["war_leader", "oath_bearer", "oath_priest"],
  },
];

export function tierById(id: string): OrgTier | undefined {
  return ORG_TIERS.find((t) => t.id === id);
}

export function tiersForFaction(factionId: string): OrgTier[] {
  return ORG_TIERS.filter((t) => t.faction === factionId).sort(
    (a, b) => a.paperStrength - b.paperStrength,
  );
}

/**
 * Muster sets per faction per echelon — PROTOTYPE transcription of the
 * `echelon_variants.*.muster` arrays in proposed-organization*.json
 * (finalized 2026-09-21). Reforms REPLACE earlier tiers (Gaul E4 drops the
 * warband for the coalition shieldband); the player chooses a tier from
 * the set at muster time. Tier IDs are the prototype's prefixed save IDs.
 */
export const MUSTER_SETS: Record<string, Record<number, string[]>> = {
  rome: {
    1: ["rome_contubernium"],
    2: ["rome_century"],
    3: ["rome_century", "rome_cohort", "rome_legion"],
    4: ["rome_century", "rome_cohort", "rome_legion"],
    // E5 vexillatio/field army are PENDING HISTORICAL REVIEW — not
    // musterable in this prototype; E4 sets carry forward.
    5: ["rome_century", "rome_cohort", "rome_legion"],
  },
  gauls: {
    1: ["gaul_warband"],
    2: ["gaul_warband", "gaul_host"],
    3: ["gaul_warband", "gaul_host"],
    4: ["gaul_shieldband", "gaul_host"],
    5: ["gaul_shieldband", "gaul_grand_host"],
  },
  samnite: {
    1: ["samnite_warband"],
    2: ["samnite_warband", "samnite_linen_legion"],
    3: ["samnite_warband", "samnite_contingent", "samnite_linen_legion"],
    4: ["samnite_federal_warband", "samnite_contingent", "samnite_linen_legion"],
    5: ["samnite_federal_warband", "samnite_layered_host", "samnite_linen_legion"],
  },
};

/**
 * Tiers the player may muster at this echelon: the echelon's muster set
 * filtered by researched tech gates. Falls back to the nearest lower
 * echelon with a non-empty filtered set (e.g. E2 Rome without Manipular
 * Drill still levies contubernia). Sorted by paper strength ascending.
 */
export function musterTiers(
  factionId: string,
  echelonIdx: number,
  completedTechs: Set<string>,
): OrgTier[] {
  const sets = MUSTER_SETS[factionId];
  if (!sets) return [];
  for (let e = Math.min(echelonIdx, 5); e >= 1; e--) {
    const ids = sets[e] ?? [];
    const tiers: OrgTier[] = [];
    for (const id of ids) {
      const t = tierById(id);
      if (!t) continue;
      if (t.techGate && !completedTechs.has(t.techGate)) continue;
      tiers.push(t);
    }
    if (tiers.length > 0) {
      return tiers.sort((a, b) => a.paperStrength - b.paperStrength);
    }
  }
  return [];
}

/**
 * Verify a tier's provable composition:
 * - leaf tier: rankers + non-visual appointments === fieldBodies
 * - composite tier: Σ(count × sub-tier fieldBodies) + commandGroup === fieldBodies
 * Returns the expected field bodies, or null if the tier has no
 * composition data to prove.
 */
export function proveComposition(tier: OrgTier): number | null {
  if (tier.contains && tier.contains.length > 0) {
    let n = tier.commandGroup ?? 0;
    for (const sub of tier.contains) {
      const st = tierById(sub.tierId);
      if (!st) return null;
      n += sub.count * st.fieldBodies;
    }
    return n;
  }
  if (tier.rankers !== undefined) {
    const officers = tier.appointments.filter(
      (a) => !a.endsWith("(visual)"),
    ).length;
    return tier.rankers + officers;
  }
  return null;
}

/**
 * Highest tier the faction can muster: every tier whose tech gate is
 * researched (or absent), by paper strength. The AI drills without age-up,
 * so it uses this (echelon-ignored) — "the best unlocked
 * faction-appropriate tier". The player instead chooses from
 * musterTiers().
 */
export function bestTierForFaction(
  factionId: string,
  completedTechs: Set<string>,
): OrgTier {
  const tiers = tiersForFaction(factionId);
  let best: OrgTier | undefined;
  for (const t of tiers) {
    if (t.techGate && !completedTechs.has(t.techGate)) continue;
    if (!best || t.paperStrength > best.paperStrength) best = t;
  }
  // Unknown faction (no tier table): fall back to the pre-organization
  // 60-man warband so callers never see undefined.
  return (
    best ?? {
      id: "",
      faction: factionId,
      name_en: "Warband",
      paperStrength: BASE_FORMATION_SIZE,
      fieldBodies: BASE_FORMATION_SIZE,
      techGate: null,
      minEchelon: 1,
      appointments: [],
    }
  );
}

/** AI org-tech lines in research order (proposed canonical IDs). */
export const ORG_TECH_LINE: Record<string, string[]> = {
  // Rome drills manipular -> cohortal; the E5 field-army tech gates
  // nothing musterable in this prototype (vexillatio/field army are
  // PENDING HISTORICAL REVIEW), so it stays out of the AI's drill order.
  rome: ["tech_manipular_drill", "tech_cohort_organisation"],
  gauls: [
    "tech_gaul_oppidum_hall_drill",
    "tech_gaul_coalition_muster",
    "tech_gaul_grand_coalition_compact",
  ],
  samnite: [
    "tech_samnite_sacred_precinct_drill",
    "tech_samnite_war_camp_drill",
    "tech_samnite_federal_muster",
    "tech_samnite_depth_defence",
  ],
};

/** Upkeep food/min for a formation of paper strength n. */
export function formationUpkeepPerMin(paperStrength: number): number {
  if (paperStrength <= 0) return 0;
  return UPKEEP_K * Math.pow(paperStrength, UPKEEP_ALPHA);
}

/** Upkeep food/sec (economy-tick units). */
export function formationUpkeepPerSec(paperStrength: number): number {
  return formationUpkeepPerMin(paperStrength) / 60;
}

/** Training cost/time multiplier for mustering at this tier. */
export function trainScale(tier: OrgTier): number {
  return tier.paperStrength / BASE_FORMATION_SIZE;
}

/**
 * Scale a training cost record by the tier's muster multiplier (ceil per
 * resource so small costs stay non-zero). Muster rolls are honest: a
 * legion costs ~83x a warband to raise.
 */
export function tierTrainCost(
  base: Record<string, number>,
  tier: OrgTier,
): Record<string, number> {
  const s = trainScale(tier);
  const out: Record<string, number> = {};
  for (const [id, v] of Object.entries(base)) out[id] = Math.ceil(v * s);
  return out;
}

/**
 * Single-figure retinues (commanders and their staff) pay upkeep on
 * actual heads, not on a tier's paper strength — a lone chieftain does
 * not eat like a century.
 */
export function isSingleFigureRole(role: string | undefined): boolean {
  return role === "commander";
}

/** Paper strength for a newly mustered formation. */
export function resolvePaperStrength(
  unit: UnitDef,
  tier: OrgTier | undefined,
  fieldBodies: number,
): number {
  const role = unit.extra["role"] as string | undefined;
  if (isSingleFigureRole(role)) return Math.max(1, fieldBodies);
  // Unknown tier (e.g. a pre-organization save): pay on actual bodies,
  // never zero.
  return tier?.paperStrength ?? Math.max(1, fieldBodies);
}

export interface UpkeepTickResult {
  /** Total food/sec assessed across the faction's formations. */
  totalDuePerSec: number;
  /** Fraction of this tick's upkeep the stockpile covered (1 = fully paid). */
  paidRatio: number;
}

/**
 * Assess and deduct formation upkeep for one faction, then apply the
 * failure mode: shortfall drains morale (rout checks apply), arrears
 * accumulate per formation, and sustained arrears cause desertion —
 * a trickle of bodies, never instant deletion. Formations that desert
 * down to zero bodies are swept by the caller's normal dead-fighter
 * pass.
 *
 * @param deductFood deducts up to `amount` food from the stockpile and
 *   returns what was actually paid (lets AI/player share one path).
 * @param upkeepMult tech upkeep_cost multiplier (Frumentatio lands here
 *   when the data adopts it).
 * @param handicapMult AI difficulty logistics handicap (player passes 1).
 */
export function tickFormationUpkeep(
  dt: number,
  fighters: Combatant[],
  team: 0 | 1,
  deductFood: (amount: number) => number,
  upkeepMult: number,
  handicapMult = 1,
): UpkeepTickResult {
  const own = fighters.filter((f) => f.team === team && f.alive > 0);
  const dues = own.map((f) => ({
    f,
    due: formationUpkeepPerSec(f.paperStrength) * upkeepMult * handicapMult * dt,
  }));
  const totalDue = dues.reduce((n, d) => n + d.due, 0);
  let paidRatio = 1;
  if (totalDue > 0) {
    const paid = deductFood(totalDue);
    paidRatio = Math.min(1, paid / totalDue);
  }
  for (const { f, due } of dues) {
    const shortfall = due * (1 - paidRatio);
    if (shortfall <= 0) {
      // Funded: arrears pay down at the formation's own burn rate.
      f.supplyDebt = Math.max(0, f.supplyDebt - due);
      f.unpaid = false;
      continue;
    }
    f.supplyDebt += shortfall;
    f.unpaid = true;
    applyUnpaidMorale(f, dt, 1 - paidRatio);
    const perMin =
      formationUpkeepPerMin(f.paperStrength) * upkeepMult * handicapMult;
    if (f.supplyDebt > perMin * DESERTION_DEBT_MINUTES && f.alive > 0) {
      f.desertFrac += f.alive * DESERTION_FRACTION_PER_SEC * dt;
      const whole = Math.floor(f.desertFrac);
      if (whole > 0) {
        f.alive = Math.max(0, f.alive - whole);
        f.desertFrac -= whole;
        if (f.alive === 0) f.deserted = true;
      }
    }
  }
  return {
    totalDuePerSec:
      totalDue > 0 ? totalDue / Math.max(dt, 1e-9) : 0,
    paidRatio,
  };
}
